import type { SessionUpdateInput } from "@/types/session"
import { memo, useEffect, useId, useMemo, useState } from "react"

import {
  Controller,
  type SubmitHandler,
  useForm,
  useWatch,
} from "react-hook-form"
import type { Resolver } from "react-hook-form"

import {
  Box,
  Button,
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPositioner,
  DialogRoot,
  DialogTitle,
  HStack,
  Input,
  Portal,
  Switch,
  Textarea,
  VStack,
} from "@chakra-ui/react"

import { Field } from "@/components/atoms/Field"

// Helper functions for ISO week calculations
function getISOWeek(date: Date): number {
  const target = new Date(date.valueOf())
  const dayNr = (date.getDay() + 6) % 7
  target.setDate(target.getDate() - dayNr + 3)
  const firstThursday = target.valueOf()
  target.setMonth(0, 1)
  if (target.getDay() !== 4) {
    target.setMonth(0, 1 + ((4 - target.getDay() + 7) % 7))
  }
  return 1 + Math.ceil((firstThursday - target.valueOf()) / 604800000)
}

function getMondayOfISOWeek(year: number, week: number): Date {
  // ISO 8601: Week 1 is the week with the year's first Thursday
  // Equivalently: the week containing January 4th

  // Find January 4th (always in ISO week 1)
  const jan4 = new Date(year, 0, 4)
  const jan4DayOfWeek = jan4.getDay() // 0=Sunday, 1=Monday, etc.

  // Find Monday of ISO week 1
  // Monday is day 1, so we need to go back (jan4DayOfWeek - 1) days
  // But if jan4 is Sunday (0), we need to go back 6 days
  const daysFromMonday = jan4DayOfWeek === 0 ? 6 : jan4DayOfWeek - 1

  // Get Monday of week 1
  const week1Monday = new Date(year, 0, 4 - daysFromMonday)

  // Add weeks to get target Monday
  const result = new Date(week1Monday)
  result.setDate(week1Monday.getDate() + (week - 1) * 7)

  return result
}

function dateToYearWeek(dateStr: string | undefined): {
  year: number
  week: number
} {
  // Handle undefined, null, or empty string
  if (!dateStr || dateStr.trim() === "") {
    const now = new Date()
    return { year: now.getFullYear(), week: getISOWeek(now) }
  }
  const date = new Date(dateStr)
  // Check for invalid date
  if (Number.isNaN(date.getTime())) {
    const now = new Date()
    return { year: now.getFullYear(), week: getISOWeek(now) }
  }
  return { year: date.getFullYear(), week: getISOWeek(date) }
}

function formatDateToISO(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

const skipLinkStyle = {
  position: "absolute",
  left: "-9999px",
  top: "-9999px",
  background: "#3182ce",
  color: "white",
  padding: "8px 16px",
  textDecoration: "none",
  borderRadius: "6px",
  zIndex: 1000,
} as const

type SessionFormValues = SessionUpdateInput

interface SessionFormDialogProps {
  isOpen: boolean
  title: string
  submitLabel: string
  resolver: Resolver<SessionFormValues>
  defaultValues: SessionFormValues
  onSubmit: (values: SessionFormValues) => Promise<void>
  onClose: () => void
  isSubmitting?: boolean
  cancelLabel?: string
  finalFocusRef?: React.RefObject<HTMLElement | null>
  showSkipLink?: boolean
  loadingText?: string
}

// Generate year options (current year - 1 to current year + 3)
const currentYear = new Date().getFullYear()
const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - 1 + i)

// Generate week options (1-52)
const weekOptions = Array.from({ length: 52 }, (_, i) => i + 1)

const SessionFormDialog = memo(
  ({
    isOpen,
    title,
    submitLabel,
    resolver,
    defaultValues,
    onSubmit,
    onClose,
    isSubmitting = false,
    cancelLabel = "Cancel",
    finalFocusRef,
    showSkipLink = false,
    loadingText,
  }: SessionFormDialogProps) => {
    const formSectionId = useId()

    const normalizedDefaults = useMemo<SessionFormValues>(
      () => ({
        name: defaultValues.name ?? "",
        slug: defaultValues.slug ?? "",
        description: defaultValues.description ?? null,
        session_type: defaultValues.session_type ?? null,
        start_week: defaultValues.start_week ?? "",
        end_week: defaultValues.end_week ?? "",
        registration_opens: defaultValues.registration_opens ?? "",
        registration_closes: defaultValues.registration_closes ?? "",
        early_bird_deadline: defaultValues.early_bird_deadline ?? null,
        makeup_classes_allowed: defaultValues.makeup_classes_allowed ?? null,
        total_classes: defaultValues.total_classes ?? null,
        no_class_dates: defaultValues.no_class_dates ?? null,
        is_active: defaultValues.is_active ?? false,
        is_published: defaultValues.is_published ?? false,
        notes: defaultValues.notes ?? null,
      }),
      [defaultValues],
    )

    // State for week pickers
    const startWeekData = useMemo(
      () => dateToYearWeek(defaultValues.start_week),
      [defaultValues.start_week],
    )
    const endWeekData = useMemo(
      () => dateToYearWeek(defaultValues.end_week),
      [defaultValues.end_week],
    )

    const [startYear, setStartYear] = useState(startWeekData.year)
    const [startWeekNum, setStartWeekNum] = useState(startWeekData.week)
    const [endYear, setEndYear] = useState(endWeekData.year)
    const [endWeekNum, setEndWeekNum] = useState(endWeekData.week)

    const form = useForm<SessionFormValues>({
      resolver,
      mode: "onBlur",
      defaultValues: normalizedDefaults,
    })

    useEffect(() => {
      if (isOpen) {
        // Reset week pickers to default values
        setStartYear(startWeekData.year)
        setStartWeekNum(startWeekData.week)
        setEndYear(endWeekData.year)
        setEndWeekNum(endWeekData.week)

        // Calculate dates from year/week and include in reset
        const startMonday = getMondayOfISOWeek(
          startWeekData.year,
          startWeekData.week,
        )
        const endMonday = getMondayOfISOWeek(endWeekData.year, endWeekData.week)

        void form.reset({
          ...normalizedDefaults,
          start_week: formatDateToISO(startMonday),
          end_week: formatDateToISO(endMonday),
        })
      }
    }, [form, isOpen, normalizedDefaults, startWeekData, endWeekData])

    // Update start_week date when year/week changes
    useEffect(() => {
      if (startYear && startWeekNum) {
        const monday = getMondayOfISOWeek(startYear, startWeekNum)
        const dateStr = formatDateToISO(monday)
        form.setValue("start_week", dateStr, { shouldValidate: true })
      }
    }, [startYear, startWeekNum, form])

    // Update end_week date when year/week changes
    useEffect(() => {
      if (endYear && endWeekNum) {
        const monday = getMondayOfISOWeek(endYear, endWeekNum)
        const dateStr = formatDateToISO(monday)
        form.setValue("end_week", dateStr, { shouldValidate: true })
      }
    }, [endYear, endWeekNum, form])

    // Watch start_week and end_week for auto-calculation
    const startWeek = useWatch({ control: form.control, name: "start_week" })
    const endWeek = useWatch({ control: form.control, name: "end_week" })

    // Auto-calculate total_classes when dates change
    useEffect(() => {
      if (startWeek && endWeek) {
        try {
          const start = new Date(startWeek)
          const end = new Date(endWeek)

          // Validate dates
          if (start >= end) {
            return // Invalid range, don't calculate
          }

          // Calculate number of weeks between two Mondays (inclusive)
          // Both dates should be Mondays representing week boundaries
          const diffInMs = end.getTime() - start.getTime()
          const diffInDays = diffInMs / (1000 * 60 * 60 * 24)

          // Since both dates are week boundaries (Mondays), calculate weeks directly
          // Example: Sep 1 to Dec 8 = 98 days = 14 weeks
          const weeks = Math.round(diffInDays / 7) + 1 // +1 to include the starting week

          // Update the total_classes field
          form.setValue("total_classes", weeks, { shouldValidate: true })
        } catch (error) {
          // Invalid date format, ignore
        }
      }
    }, [startWeek, endWeek, form])

    const handleClose = () => {
      form.reset(normalizedDefaults)
      onClose()
    }

    const handleSubmit: SubmitHandler<SessionFormValues> = async (values) => {
      try {
        await onSubmit(values)
        handleClose()
      } catch (error) {
        // Error handling is delegated to the mutation layer
      }
    }

    return (
      <DialogRoot
        open={isOpen}
        onOpenChange={(details) => {
          if (!details.open) {
            handleClose()
          }
        }}
        size={{ base: "full", md: "md" }}
        trapFocus
        restoreFocus
        initialFocusEl={() => null}
        finalFocusEl={() => finalFocusRef?.current ?? null}
      >
        <Portal>
          <DialogBackdrop />
          <DialogPositioner
            inset="0"
            display="grid"
            placeItems="center"
            padding={{ base: 4, md: 6 }}
          >
            <DialogContent
              as="form"
              onSubmit={form.handleSubmit(handleSubmit)}
              maxW="4xl"
              w="full"
              maxH="90vh"
              display="flex"
              flexDirection="column"
            >
              {showSkipLink && (
                <a
                  href={`#${formSectionId}`}
                  style={skipLinkStyle}
                  onFocus={(event) => {
                    event.currentTarget.style.left = "16px"
                    event.currentTarget.style.top = "16px"
                  }}
                  onBlur={(event) => {
                    event.currentTarget.style.left = "-9999px"
                    event.currentTarget.style.top = "-9999px"
                  }}
                >
                  Skip to form fields
                </a>
              )}

              <DialogHeader>
                <DialogTitle>{title}</DialogTitle>
                <DialogCloseTrigger />
              </DialogHeader>

              <DialogBody id={formSectionId} overflowY="auto" flex="1">
                <VStack gap={6}>
                  <Field
                    required
                    invalid={!!form.formState.errors.name}
                    errorText={
                      form.formState.errors.name?.message as string | undefined
                    }
                    label="Name"
                  >
                    <Input
                      placeholder={"Enter name"}
                      aria-invalid={!!form.formState.errors.name}
                      {...form.register("name")}
                    />
                  </Field>

                  <Field
                    required
                    invalid={!!form.formState.errors.slug}
                    errorText={
                      form.formState.errors.slug?.message as string | undefined
                    }
                    label="Slug"
                  >
                    <Input
                      placeholder={"Enter slug"}
                      aria-invalid={!!form.formState.errors.slug}
                      {...form.register("slug")}
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.description}
                    errorText={
                      form.formState.errors.description?.message as
                        | string
                        | undefined
                    }
                    label="Description"
                  >
                    <Textarea
                      {...form.register("description")}
                      placeholder={"Enter description"}
                      rows={3}
                      resize="vertical"
                      aria-invalid={!!form.formState.errors.description}
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.session_type}
                    errorText={
                      form.formState.errors.session_type?.message as
                        | string
                        | undefined
                    }
                    label="Session Type"
                  >
                    <select
                      {...form.register("session_type", {
                        setValueAs: (value) => (value === "" ? null : value),
                      })}
                      aria-invalid={!!form.formState.errors.session_type}
                      className="chakra-select"
                    >
                      <option value="">Select Session Type</option>
                      <option value="regular_season">Regular Season</option>
                      <option value="summer_camp">Summer Camp</option>
                      <option value="winter_intensive">Winter Intensive</option>
                      <option value="spring_break">Spring Break</option>
                    </select>
                  </Field>

                  <Field
                    required
                    invalid={!!form.formState.errors.start_week}
                    errorText={
                      form.formState.errors.start_week?.message as
                        | string
                        | undefined
                    }
                    label="Start Week"
                    helperText={
                      startWeek
                        ? `Monday: ${new Date(startWeek).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}`
                        : "Select year and week number"
                    }
                  >
                    <HStack gap={2}>
                      <Box flex={1}>
                        <select
                          value={startYear}
                          onChange={(e) => setStartYear(Number(e.target.value))}
                          className="chakra-select"
                          style={{ width: "100%" }}
                        >
                          {yearOptions.map((year) => (
                            <option key={year} value={year}>
                              {year}
                            </option>
                          ))}
                        </select>
                      </Box>
                      <Box flex={1}>
                        <select
                          value={startWeekNum}
                          onChange={(e) =>
                            setStartWeekNum(Number(e.target.value))
                          }
                          className="chakra-select"
                          style={{ width: "100%" }}
                        >
                          {weekOptions.map((week) => (
                            <option key={week} value={week}>
                              Week {week}
                            </option>
                          ))}
                        </select>
                      </Box>
                    </HStack>
                    {/* Hidden input to store the actual date */}
                    <Input type="hidden" {...form.register("start_week")} />
                  </Field>

                  <Field
                    required
                    invalid={!!form.formState.errors.end_week}
                    errorText={
                      form.formState.errors.end_week?.message as
                        | string
                        | undefined
                    }
                    label="End Week"
                    helperText={
                      endWeek
                        ? `Monday: ${new Date(endWeek).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}`
                        : "Select year and week number"
                    }
                  >
                    <HStack gap={2}>
                      <Box flex={1}>
                        <select
                          value={endYear}
                          onChange={(e) => setEndYear(Number(e.target.value))}
                          className="chakra-select"
                          style={{ width: "100%" }}
                        >
                          {yearOptions.map((year) => (
                            <option key={year} value={year}>
                              {year}
                            </option>
                          ))}
                        </select>
                      </Box>
                      <Box flex={1}>
                        <select
                          value={endWeekNum}
                          onChange={(e) =>
                            setEndWeekNum(Number(e.target.value))
                          }
                          className="chakra-select"
                          style={{ width: "100%" }}
                        >
                          {weekOptions.map((week) => (
                            <option key={week} value={week}>
                              Week {week}
                            </option>
                          ))}
                        </select>
                      </Box>
                    </HStack>
                    {/* Hidden input to store the actual date */}
                    <Input type="hidden" {...form.register("end_week")} />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.registration_opens}
                    errorText={
                      form.formState.errors.registration_opens?.message as
                        | string
                        | undefined
                    }
                    label="Registration Opens"
                    helperText="Optional: When registration becomes available"
                  >
                    <Input
                      type="date"
                      placeholder={"Enter registration opens"}
                      aria-invalid={!!form.formState.errors.registration_opens}
                      {...form.register("registration_opens")}
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.registration_closes}
                    errorText={
                      form.formState.errors.registration_closes?.message as
                        | string
                        | undefined
                    }
                    label="Registration Closes"
                    helperText="Optional: Last day to register"
                  >
                    <Input
                      type="date"
                      placeholder={"Enter registration closes"}
                      aria-invalid={!!form.formState.errors.registration_closes}
                      {...form.register("registration_closes")}
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.early_bird_deadline}
                    errorText={
                      form.formState.errors.early_bird_deadline?.message as
                        | string
                        | undefined
                    }
                    label="Early Bird Deadline"
                  >
                    <Input
                      type="date"
                      placeholder={"Enter early bird deadline"}
                      aria-invalid={!!form.formState.errors.early_bird_deadline}
                      {...form.register("early_bird_deadline")}
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.makeup_classes_allowed}
                    errorText={
                      form.formState.errors.makeup_classes_allowed?.message as
                        | string
                        | undefined
                    }
                    label="Makeup Classes Allowed"
                  >
                    <Input
                      type="number"
                      step={1}
                      placeholder={"Enter makeup classes allowed"}
                      aria-invalid={
                        !!form.formState.errors.makeup_classes_allowed
                      }
                      {...form.register("makeup_classes_allowed", {
                        valueAsNumber: true,
                      })}
                    />
                  </Field>

                  <Field
                    required
                    invalid={!!form.formState.errors.total_classes}
                    errorText={
                      form.formState.errors.total_classes?.message as
                        | string
                        | undefined
                    }
                    label="Total Classes"
                    helperText="Total number of class meetings in this session"
                  >
                    <Input
                      type="number"
                      step={1}
                      placeholder={"Enter total classes"}
                      aria-invalid={!!form.formState.errors.total_classes}
                      {...form.register("total_classes", {
                        valueAsNumber: true,
                      })}
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.no_class_dates}
                    errorText={
                      form.formState.errors.no_class_dates?.message as
                        | string
                        | undefined
                    }
                    label="No Class Dates"
                  >
                    <Input
                      placeholder={"Enter no class dates"}
                      aria-invalid={!!form.formState.errors.no_class_dates}
                      {...form.register("no_class_dates")}
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.is_active}
                    errorText={
                      form.formState.errors.is_active?.message as
                        | string
                        | undefined
                    }
                    label="Is Active"
                  >
                    <Controller
                      name="is_active"
                      control={form.control}
                      render={({ field: controllerField }) => (
                        <Switch.Root
                          id="is_active"
                          checked={!!controllerField.value}
                          onCheckedChange={({ checked }) =>
                            controllerField.onChange(checked)
                          }
                          aria-label="Is Active"
                        >
                          <Switch.HiddenInput
                            ref={controllerField.ref}
                            name={controllerField.name}
                            onBlur={controllerField.onBlur}
                          />
                          <Switch.Control>
                            <Switch.Thumb />
                          </Switch.Control>
                        </Switch.Root>
                      )}
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.is_published}
                    errorText={
                      form.formState.errors.is_published?.message as
                        | string
                        | undefined
                    }
                    label="Is Published"
                  >
                    <Controller
                      name="is_published"
                      control={form.control}
                      render={({ field: controllerField }) => (
                        <Switch.Root
                          id="is_published"
                          checked={!!controllerField.value}
                          onCheckedChange={({ checked }) =>
                            controllerField.onChange(checked)
                          }
                          aria-label="Is Published"
                        >
                          <Switch.HiddenInput
                            ref={controllerField.ref}
                            name={controllerField.name}
                            onBlur={controllerField.onBlur}
                          />
                          <Switch.Control>
                            <Switch.Thumb />
                          </Switch.Control>
                        </Switch.Root>
                      )}
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.notes}
                    errorText={
                      form.formState.errors.notes?.message as string | undefined
                    }
                    label="Notes"
                  >
                    <Textarea
                      {...form.register("notes")}
                      placeholder={"Enter notes"}
                      rows={3}
                      resize="vertical"
                      aria-invalid={!!form.formState.errors.notes}
                    />
                  </Field>
                </VStack>
              </DialogBody>

              <DialogFooter>
                <HStack gap={3}>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    {cancelLabel}
                  </Button>
                  <Button
                    type="submit"
                    colorPalette="blue"
                    loading={isSubmitting}
                    loadingText={loadingText ?? `${submitLabel}...`}
                  >
                    {submitLabel}
                  </Button>
                </HStack>
              </DialogFooter>
            </DialogContent>
          </DialogPositioner>
        </Portal>
      </DialogRoot>
    )
  },
)

SessionFormDialog.displayName = "SessionFormDialog"

export default SessionFormDialog
